/**
 * notificationSound.ts — motor de som de notificação in-app (Web Audio API).
 *
 * Spec: docs/superpowers/specs/2026-07-09-chat-notification-sound-design.md §1/§4.
 *
 * - SEM asset binário: sintetiza os toques via OscillatorNode + GainNode, o que
 *   funciona offline no PWA, é minúsculo e permite ajuste fino dos timbres.
 * - AudioContext singleton lazy: só é criado no 1º gesto do usuário
 *   (limitação de autoplay dos navegadores). Antes do unlock, `playSound` é
 *   no-op silencioso.
 * - Sem suporte a Web Audio / contexto bloqueado → no-op (nunca lança).
 * - Preferência por dispositivo em localStorage (`consoleai.sound.enabled`,
 *   default ON). Sem backend/migration.
 */

export type SoundKind = 'message' | 'alert_info' | 'alert_warning' | 'alert_critical'

const STORAGE_KEY = 'consoleai.sound.enabled'

// AudioContext singleton — criado/resumido no 1º gesto (unlockAudio).
let ctx: AudioContext | null = null
let unlockBound = false

type AudioContextCtor = typeof AudioContext

function getAudioContextCtor(): AudioContextCtor | null {
  if (typeof window === 'undefined') return null
  const w = window as unknown as {
    AudioContext?: AudioContextCtor
    webkitAudioContext?: AudioContextCtor
  }
  return w.AudioContext ?? w.webkitAudioContext ?? null
}

/**
 * unlockAudio — arma um listener único (once) em `pointerdown`/`keydown` que
 * cria (ou resume) o AudioContext no 1º gesto do usuário. Idempotente: chamar
 * várias vezes (mount de hook/módulo) não duplica listeners.
 */
export function unlockAudio(): void {
  if (unlockBound) return
  if (typeof window === 'undefined' || typeof document === 'undefined') return
  const Ctor = getAudioContextCtor()
  if (!Ctor) return
  unlockBound = true

  const unlock = () => {
    try {
      if (!ctx) ctx = new Ctor()
      if (ctx.state === 'suspended') void ctx.resume()
    } catch {
      /* sem suporte / bloqueado — no-op */
    }
  }

  try {
    document.addEventListener('pointerdown', unlock, { once: true })
    document.addEventListener('keydown', unlock, { once: true })
  } catch {
    /* ambiente sem addEventListener — no-op */
  }
}

/**
 * Toca uma sequência de notas curtas (envelope attack/decay) no AudioContext.
 * Cada nota: oscilador → gain (ganho por-nota) → destino.
 */
function playNotes(
  notes: { freq: number; start: number; dur: number; type?: OscillatorType; gain?: number }[],
): void {
  if (!ctx || ctx.state !== 'running') return
  const now = ctx.currentTime
  const attack = 0.01
  const decay = 0.15
  for (const n of notes) {
    const osc = ctx.createOscillator()
    const g = ctx.createGain()
    osc.type = n.type ?? 'sine'
    osc.frequency.value = n.freq
    const peak = n.gain ?? 0.18
    const t0 = now + n.start
    // Envelope curto: silêncio → attack → decay até ~0.
    g.gain.setValueAtTime(0.0001, t0)
    g.gain.exponentialRampToValueAtTime(peak, t0 + attack)
    g.gain.exponentialRampToValueAtTime(0.0001, t0 + attack + decay)
    osc.connect(g)
    g.connect(ctx.destination)
    osc.start(t0)
    osc.stop(t0 + n.dur + 0.02)
  }
}

/**
 * playSound(kind) — sintetiza a assinatura ConsoleAI de acordo com o tipo.
 * Todos os timbres são curtos (<0.6s) e suaves. Ajustáveis por ouvido.
 * No-op silencioso se o áudio não estiver desbloqueado ou sem suporte.
 */
export function playSound(kind: SoundKind): void {
  try {
    if (!ctx || ctx.state !== 'running') return
    switch (kind) {
      case 'message':
        // Assinatura: 3 notas ascendentes suaves (A5 → C#6 → E6), tipo triangle.
        playNotes([
          { freq: 880, start: 0.0, dur: 0.12, type: 'triangle', gain: 0.16 },
          { freq: 1108.73, start: 0.11, dur: 0.12, type: 'triangle', gain: 0.16 },
          { freq: 1318.51, start: 0.22, dur: 0.14, type: 'triangle', gain: 0.16 },
        ])
        break
      case 'alert_info':
        // 1 nota curta discreta (~660 Hz).
        playNotes([{ freq: 660, start: 0.0, dur: 0.16, type: 'sine', gain: 0.18 }])
        break
      case 'alert_warning':
        // 2 toques médios (~520 Hz).
        playNotes([
          { freq: 520, start: 0.0, dur: 0.14, type: 'sine', gain: 0.2 },
          { freq: 520, start: 0.2, dur: 0.14, type: 'sine', gain: 0.2 },
        ])
        break
      case 'alert_critical':
        // 3 toques mais graves e rápidos (~330 Hz) — mais insistente.
        playNotes([
          { freq: 330, start: 0.0, dur: 0.12, type: 'square', gain: 0.16 },
          { freq: 330, start: 0.14, dur: 0.12, type: 'square', gain: 0.16 },
          { freq: 330, start: 0.28, dur: 0.12, type: 'square', gain: 0.16 },
        ])
        break
    }
  } catch {
    /* sem Web Audio / contexto bloqueado — no-op silencioso */
  }
}

/**
 * getSoundEnabled — preferência por dispositivo (localStorage). Default ON.
 */
export function getSoundEnabled(): boolean {
  try {
    if (typeof localStorage === 'undefined') return true
    const v = localStorage.getItem(STORAGE_KEY)
    if (v === null) return true // default ON
    return v === 'true'
  } catch {
    return true
  }
}

/**
 * setSoundEnabled — persiste a preferência por dispositivo.
 */
export function setSoundEnabled(enabled: boolean): void {
  try {
    if (typeof localStorage === 'undefined') return
    localStorage.setItem(STORAGE_KEY, enabled ? 'true' : 'false')
  } catch {
    /* localStorage indisponível (modo privado etc.) — no-op */
  }
}
